import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import lodashFilter from 'lodash/filter';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../Config/constants';
import { EventTimeSlot } from '../RTK/api/events/types';
import { DBDATE, DBTIME } from '../types/date';

// Extend dayjs with the plugins for isOlderThanNow
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const convertNumberToDay = (dayInNumber: number) => {
  return (
    ['Sun', 'Mon', 'Tues', 'Wednes', 'Thurs', 'Fri', 'Satur'][dayInNumber] +
    'day'
  );
};

type GetDayjsAtTime = (militaryTime?: string, date?: string) => dayjs.Dayjs;
const getDayjsAtTime: GetDayjsAtTime = (
  militaryTime = '',
  date = undefined,
) => {
  const [hour, min] = militaryTime.split(':').map(Number);
  return dayjs(date)
    .set('hour', hour)
    .set('minute', min)
    .second(0)
    .millisecond(0);
};

const isOlderThanNow = ({
  amount,
  time,
  unit,
}: {
  amount: number;
  time: number;
  unit: ManipulateType;
}) => {
  const now = dayjs();
  const timeToCompare = dayjs(time).add(amount, unit);
  return now.isSameOrAfter(timeToCompare);
};

const formatDateRange = (startDate, endDate) => {
  const fullFormat = `ddd, MMM D ${constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT}`;
  const timeFormat = constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT;

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  return start.isSame(end, 'day')
    ? `${start.format(fullFormat)} - ${end.format(timeFormat)}`
    : `${start.format(fullFormat)} - ${end.format(fullFormat)}`;
};

const formatDateOnlyRange = (startDate, endDate) => {
  const firstFormat = 'MMM D';
  const fullFormat = 'MMM D';

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  return start.isSame(end, 'day')
    ? `${start.format(fullFormat)}`
    : `${start.format(firstFormat)} - ${end.format(fullFormat)}`;
};

const formatTimeRange = (startDate, endDate) => {
  const timeFormat = 'hh:mm A';

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  return `${start.format(timeFormat)} - ${end.format(timeFormat)}`;
};

type FormatTimeRangeArray = (slots: EventTimeSlot[]) => string;
const formatTimeRangeArray: FormatTimeRangeArray = (slots = []) => {
  if (lodashIsEmpty(slots)) return 'N/A';
  return slots
    .map((e) =>
      getDayjsAtTime(e.start_time).format(
        constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
      ),
    )
    .join(', ');
};

const formatShortDateRage = (startDate, endDate) => {
  const fullFormat = 'MMM D hh:mm A';
  const timeFormat = 'hh:mm';

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  return start.isSame(end, 'day')
    ? `${start.format(fullFormat)} - ${end.format(timeFormat)}`
    : `${start.format(fullFormat)} - ${end.format(fullFormat)}`;
};

const dateToFormat = (date) => {
  return dayjs(date).format('MMM D');
};

/**
 * Generate timeslots for today base on current time until 11:30 PM
 * @param {Dayjs} selectedDate - optional, if pass instead of current date and time, this will use as base date and time
 * @returns {string[]}
 */
const getTimeSlots = (selectedDate?: Dayjs): string[] => {
  const now = dayjs();
  const selected = dayjs(selectedDate);
  const slots: string[] = [];

  const startIfNotSameDay = selected.startOf('day');
  let start = selected.isSame(now, 'day')
    ? (now.minute() < 30
        ? now.minute(30)
        : now.add(1, 'hour').minute(0)
      ).second(0)
    : startIfNotSameDay;
  const end = selected.endOf('day');

  while (start.isBefore(end)) {
    slots.push(start.format(constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT));
    start = start.add(30, 'minute');
  }

  return slots;
};

const getTimeSlotsByPrefferedTime = (
  startDate?: Dayjs,
  endDate?: Dayjs,
): string[] => {
  const slots: string[] = [];

  const start = startDate ? dayjs(startDate) : dayjs().startOf('day');
  const end = endDate ? dayjs(endDate) : start.endOf('day');

  let current = start.clone();
  while (current.isBefore(end)) {
    slots.push(current.format(constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT));
    current = current.add(30, 'minute');
  }

  return slots;
};

/**
 * Return next 30 min. time slot e.g: current time 11:28 -> 11:30, 11:31 -> 12:00
 * @param {dayjs.Dayjs} date
 * @returns {dayjs.Dayjs}
 */
const getToNextSlot = (date: dayjs.Dayjs): dayjs.Dayjs => {
  const minutes = date.minute();
  const remainder = minutes % 30;
  const addMinutes = remainder === 0 ? 0 : 30 - remainder;
  return date.add(addMinutes, 'minute').second(0).millisecond(0);
};

type GetEventScheduleInfoType = (
  startDate: string,
  endDate: string,
  timeSlots: EventTimeSlot[],
  availableDays: number[],
) => {
  isFinalDayOngoing: boolean;
  isLastStart: boolean;
  remainingDay: number;
  start: dayjs.Dayjs;
  startFormatted: string;
};
const getEventScheduleInfo: GetEventScheduleInfoType = (
  startDate,
  endDate,
  timeSlots = [],
  availableDays = [],
) => {
  const sortedAvailableDays = [...availableDays]?.sort?.(); // spread to prevent sort error since availableDays from api is immutable
  const sortedTimeSlots: string[] = timeSlots
    ?.map?.((ts) => ts.start_time)
    ?.sort?.();
  const filteredTimeSlots = lodashFilter(sortedTimeSlots, (ts) => {
    const tsStartDayjs = getDayjsAtTime(ts);
    const currentDayjs = dayjs();
    return currentDayjs.isSameOrBefore(tsStartDayjs);
  });
  const firstTimeSlot = filteredTimeSlots[0] || timeSlots?.[0]?.start_time; // if filteredTimeSlots is empty, we use the first time slot
  const startDayjs = getDayjsAtTime(firstTimeSlot, startDate);
  const todayStartTimedDayjs = getDayjsAtTime(firstTimeSlot);
  const endDayjs = dayjs(endDate);
  const isStartDateBefore = startDayjs.isSameOrBefore(dayjs(), 'day');
  const isStartTimeBefore = todayStartTimedDayjs.isSameOrBefore(
    dayjs(),
    'minute',
  );
  const startBase = isStartDateBefore
    ? todayStartTimedDayjs.add(isStartTimeBefore ? 1 : 0, 'day')
    : startDayjs;
  // get next available date base on availableDays
  let start = startBase;
  let tries = 0;
  while (!sortedAvailableDays.includes(start.day())) {
    start = start.add(1, 'day');
    tries++;
    if (tries > 14) break; // avoid infinite loop if config is off
  }
  // count remaining days considering the availableDays
  let remainingDay = 0;
  let current = start.clone();
  while (current.isSameOrBefore(endDayjs, 'day')) {
    if (availableDays.includes(current.day())) {
      remainingDay++;
    }
    current = current.add(1, 'day');
  }
  if (isStartTimeBefore) {
    remainingDay = Math.max(0, remainingDay - 1);
  }
  const isLastStart = endDayjs.isSame(dayjs(), 'day');
  const isFinalDayOngoing = isLastStart && isStartTimeBefore;
  const updatedStart = isFinalDayOngoing ? todayStartTimedDayjs : start;
  return {
    isFinalDayOngoing,
    isLastStart,
    remainingDay,
    start: updatedStart,
    startFormatted: updatedStart.format('MMM D, YYYY'),
  };
};

const formatDateWithText = (startDate, endDate, availableDays) => {
  const firstFormat = 'MMM D';
  const fullFormat = 'MMM D, YYYY';

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const isAvailableDaily = availableDays.length === 7;
  if (start.isSame(end, 'day')) {
    return start.format(fullFormat);
  }
  const availabilityText = isAvailableDaily ? 'Daily' : 'Select Dates';
  return `${availabilityText} from ${start.format(firstFormat)} - ${end.format(
    fullFormat,
  )}`;
};

const formatDate = (date) => {
  const fullFormat = 'MMM D, YYYY';
  return dayjs(date, 'YYYY-MM-DD').format(fullFormat);
};

export const MEAL_HOURS = {
  LUNCH: 12,
  DINNER: 19,
};

type GetNextMealTimeAsDayjs = () => Dayjs;
const getNextMealTimeAsDayjs: GetNextMealTimeAsDayjs = () => {
  const now = dayjs();
  const currHour = now.hour();
  const mealHours = Object.keys(MEAL_HOURS).map((e) => MEAL_HOURS[e]);
  for (let i = 0; i < mealHours.length; i++) {
    const hour = mealHours[i];
    if (currHour < hour) {
      return now.set('hour', hour).set('minute', 0);
    }
  }
  return getToNextSlot(now);
};
type GetNextMealTimeAsString = (f: string) => string;
const getNextMealTimeAsString: GetNextMealTimeAsString = (format) => {
  const result = getNextMealTimeAsDayjs();
  return result.format(format);
};

type DateFilter = (
  type: 'all' | 'dining' | 'event',
  dateFilter?: string, // DBDATE_FORMAT
) => DBDATE;
const getDateFilter: DateFilter = (type, dateFilter) => {
  const defaultDate =
    type === constants.RESERVATION_TYPE.EVENT
      ? dayjs()
          .add(constants.eventFilterData[1].value, 'day')
          .format(constants.DBDATE_FORMAT)
      : getToNextSlot(dayjs()).format(constants.DBDATE_FORMAT);

  return (dateFilter || defaultDate) as DBDATE;
};

type SafeSubtractOneHour = (time: DBTIME) => DBTIME; // args and return format is DBTIME_FORMAT
const safeSubtractOneHour: SafeSubtractOneHour = (time) => {
  const inputTime = getDayjsAtTime(time);
  const subtractedTime = inputTime.subtract(1, 'hour');
  const now = dayjs();
  // Only subtract if the new time is not before now
  if (subtractedTime.isAfter(now)) {
    return subtractedTime.format(constants.DBTIME_FORMAT) as DBTIME;
  }
  return time; // fallback to original if not safe
};

export default {
  dateToFormat,
  convertNumberToDay,
  getDayjsAtTime,
  isOlderThanNow,
  formatDateRange,
  formatShortDateRage,
  formatDateOnlyRange,
  formatTimeRange,
  formatTimeRangeArray,
  getTimeSlots,
  formatDateWithText,
  getEventScheduleInfo,
  formatDate,
  getToNextSlot,
  getTimeSlotsByPrefferedTime,
  getNextMealTimeAsDayjs,
  getNextMealTimeAsString,
  getDateFilter,
  safeSubtractOneHour,
};
